/**
 * Cart reservation countdown for the cart drawer.
 *
 * Shows a subtle "items reserved for MM:SS" bar at the top of the drawer.
 * The deadline is persisted in sessionStorage so the countdown stays consistent
 * across page navigations within the same browsing session, and is NOT restarted
 * once it expires (avoids the "obviously fake, resets forever" feeling). A fresh
 * session starts a new 10-minute window.
 */

const STORAGE_KEY = 'drivora:cart-reservation-deadline';
const DURATION_MS = 10 * 60 * 1000;

class CartReservationTimer extends HTMLElement {
  /** @type {number | null} */
  #intervalId = null;

  /** @type {HTMLElement | null} */
  #timeEl = null;

  /** @type {number | null} */
  #fallbackDeadline = null;

  connectedCallback() {
    this.#timeEl = this.querySelector('[data-reservation-time]');
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
  }

  /** @returns {number} The deadline timestamp in ms, or 0 if the session window already expired. */
  #getDeadline() {
    const now = Date.now();
    let stored = null;
    try {
      stored = sessionStorage.getItem(STORAGE_KEY);
    } catch {
      // sessionStorage unavailable (private mode / blocked) — fall back to an in-memory window.
      return this.#fallbackDeadline ?? (this.#fallbackDeadline = now + DURATION_MS);
    }

    if (stored === 'expired') return 0;

    const deadline = stored ? parseInt(stored, 10) : NaN;
    if (!deadline || Number.isNaN(deadline)) {
      const next = now + DURATION_MS;
      try {
        sessionStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        /* noop */
      }
      return next;
    }

    return deadline;
  }

  #start() {
    this.#stop();
    this.#render();
    this.#intervalId = window.setInterval(() => this.#render(), 1000);
  }

  #stop() {
    if (this.#intervalId != null) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }

  #render() {
    const deadline = this.#getDeadline();
    const remaining = deadline - Date.now();

    if (deadline === 0 || remaining <= 0) {
      this.#stop();
      if (deadline !== 0) {
        try {
          sessionStorage.setItem(STORAGE_KEY, 'expired');
        } catch {
          /* noop */
        }
      }
      this.hidden = true;
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (this.#timeEl) {
      this.#timeEl.textContent = `${minutes}:${String(seconds).padStart(2, '0')}`;
    }
  }
}

if (!customElements.get('cart-reservation-timer')) {
  customElements.define('cart-reservation-timer', CartReservationTimer);
}
